import { useRuntimeConfig } from '#app'
import { get } from 'lodash-es'
import { useMainStore } from '@/composables/store'
import config from '@/config/index'
import {
  getCurrentServer,
  getMallAppToken,
  getToken,
} from '@/utils/globalData'

const QUEUE_KEY = 'frontend_error_log_queue_v1'
const DEVICE_ID_KEY = 'frontend_error_log_device_id_v1'
const SESSION_KEY = 'frontend_error_log_session_v1'
const GEOIP_CACHE_KEY = 'frontend_error_log_geoip_v1'
const DEBUG_ENABLE_KEY = 'frontend_error_log_enabled'
const DEBUG_UPLOAD_URL_KEY = 'frontend_error_log_upload_url'
const ROUTE_STACK_MAX_LENGTH = 1200
const ROUTE_STACK_MAX_SIZE = 10
const SESSION_TTL_MS = 30 * 60 * 1000
const GEOIP_CACHE_TTL_MS = 24 * 60 * 60 * 1000
const BASE_RETRY_MS = 5 * 1000
const MAX_RETRY_MS = 5 * 60 * 1000
const REQUEST_TIMEOUT_MS = 15 * 1000
const WEB_KEEPALIVE_MAX_BYTES = 60 * 1024
const GEOIP_API_URL = 'https://api.ip.sb/geoip/'
const DEFAULT_IGNORE_PATHS = [
  '/frontserver/frontendErrorLog/batch',
  '/api/mall/frontserver/frontendErrorLog/batch',
  '/api/cmsadmin/frontServer/frontendErrorLog/batch',
]

const DEFAULT_CONFIG = {
  enabled: true,
  uploadUrl: null,
  flushIntervalMs: 60 * 1000,
  maxBatchSize: 20,
  maxQueueSize: 200,
  maxLocalRetentionMs: 7 * 24 * 60 * 60 * 1000,
  sampleRate: 1,
  regionDetectMode: 'light',
  captureRequestHeaders: true,
  captureRequestData: true,
  captureResponseHeaders: true,
  captureResponseData: true,
  rawPayloadMaxBytes: 65536,
  ignoreApiPaths: DEFAULT_IGNORE_PATHS,
}

let initialized = false
let queueCache = null
let flushTimer = null
let flushing = false
let online = true
let networkType = 'unknown'
let geoipFetchPromise = null
let geoipCache = null
let currentRouteMeta = {
  routePath:
    typeof window !== 'undefined'
      ? `${window.location.pathname || ''}${window.location.search || ''}`
      : undefined,
}
let currentRouteHistory = appendRouteHistory([], currentRouteMeta.routePath)

function now() {
  return Date.now()
}

function isClientRuntime() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

function safeStorageGet(key) {
  if (typeof window === 'undefined')
    return null
  try {
    return window.localStorage.getItem(key)
  }
  catch {
    return null
  }
}

function safeStorageSet(key, value) {
  if (typeof window === 'undefined')
    return
  try {
    window.localStorage.setItem(key, value)
  }
  catch {
    // Keep the in-memory queue when storage is unavailable or full.
  }
}

function safeStorageRemove(key) {
  if (typeof window === 'undefined')
    return
  try {
    window.localStorage.removeItem(key)
  }
  catch {
    // Ignore unavailable storage.
  }
}

function safeJsonParse(value, fallback) {
  if (!value)
    return fallback
  try {
    return JSON.parse(value)
  }
  catch {
    return fallback
  }
}

function normalizeBoolean(value, fallback) {
  if (value === undefined || value === null || value === '')
    return fallback
  if ([true, 1, '1', 'true', 'yes', 'on'].includes(value))
    return true
  if ([false, 0, '0', 'false', 'no', 'off'].includes(value))
    return false
  return fallback
}

function normalizeNumber(value, fallback, minValue = 1) {
  const next = Number(value)
  return Number.isFinite(next) ? Math.max(Math.floor(next), minValue) : fallback
}

function normalizeSampleRate(value, fallback) {
  const next = Number(value)
  if (!Number.isFinite(next))
    return fallback
  return Math.min(Math.max(next, 0), 1)
}

function normalizeOptionalUrl(value) {
  const text = String(value || '').trim()
  return text ? text.slice(0, 2000) : null
}

function normalizeRegionDetectMode(value) {
  return ['none', 'light', 'server_enrich'].includes(value) ? value : 'light'
}

function normalizeIgnoreApiPaths(value) {
  const values = Array.isArray(value)
    ? value.map(item => String(item || '').trim()).filter(Boolean)
    : []
  return Array.from(new Set([...DEFAULT_IGNORE_PATHS, ...values]))
}

function parseObject(value) {
  if (!value)
    return {}
  if (typeof value === 'object' && !Array.isArray(value))
    return value
  if (typeof value !== 'string')
    return {}
  const parsed = safeJsonParse(value, {})
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
    ? parsed
    : {}
}

function getDebugQueryValue(key) {
  if (typeof window === 'undefined' || !window.location)
    return null
  const rawParts = [window.location.search, window.location.hash]
    .map(value => String(value || '').replace(/^[?#]/, ''))
    .filter(Boolean)

  for (const rawPart of rawParts) {
    try {
      const params = new URLSearchParams(rawPart)
      if (params.has(key))
        return params.get(key)
    }
    catch {
      // Continue with the next URL part.
    }
  }
  return null
}

function getStoreSnapshot() {
  try {
    const store = useMainStore()
    const currentServer = getCurrentServer()
    const storeInfo
      = (typeof store.getStoreInfoDataMap === 'function'
        ? store.getStoreInfoDataMap(currentServer)
        : null) || {}
    return { store, currentServer, storeInfo }
  }
  catch {
    return { store: null, currentServer: '', storeInfo: {} }
  }
}

function getRuntimeConfigSources() {
  const { store, storeInfo } = getStoreSnapshot()
  const siteInfo = store?.$state?.siteInfo || {}
  const siteConfigData = parseObject(siteInfo.configData)
  const storePageInfo = parseObject(storeInfo.storePageInfo)
  const storeOtherJson = parseObject(storePageInfo.otherJson)
  const sources = [
    parseObject(siteConfigData.frontendErrorLogConfig),
    parseObject(siteInfo.frontendErrorLogConfig),
    parseObject(storeInfo.frontendErrorLogConfig),
    parseObject(storeOtherJson.frontendErrorLogConfig),
    parseObject(storePageInfo.frontendErrorLogConfig),
  ]
  const mergedConfig = Object.assign({}, ...sources)
  const supportFlags = [
    siteConfigData.supportFrontendErrorLog,
    siteInfo.supportFrontendErrorLog,
    storeInfo.supportFrontendErrorLog,
    storeOtherJson.supportFrontendErrorLog,
    storePageInfo.supportFrontendErrorLog,
  ].filter(value => value !== undefined && value !== null && value !== '')

  return {
    mergedConfig,
    supportFlag: supportFlags.length
      ? supportFlags[supportFlags.length - 1]
      : undefined,
  }
}

function getPublicRuntimeConfig() {
  try {
    return useRuntimeConfig().public || {}
  }
  catch {
    return {}
  }
}

export function getFrontendErrorLogConfig() {
  const publicConfig = getPublicRuntimeConfig()
  const { mergedConfig, supportFlag } = getRuntimeConfigSources()
  const defaultEnabled = normalizeBoolean(
    publicConfig.frontendErrorLogEnabled,
    DEFAULT_CONFIG.enabled,
  )
  const runtimeEnabled = normalizeBoolean(
    mergedConfig.enabled,
    normalizeBoolean(supportFlag, defaultEnabled),
  )
  const debugEnabled
    = getDebugQueryValue(DEBUG_ENABLE_KEY) ?? safeStorageGet(DEBUG_ENABLE_KEY)
  const debugUploadUrl
    = getDebugQueryValue(DEBUG_UPLOAD_URL_KEY)
      ?? safeStorageGet(DEBUG_UPLOAD_URL_KEY)

  return {
    enabled: normalizeBoolean(debugEnabled, runtimeEnabled),
    uploadUrl:
      normalizeOptionalUrl(debugUploadUrl)
      || normalizeOptionalUrl(mergedConfig.uploadUrl)
      || normalizeOptionalUrl(publicConfig.frontendErrorLogUploadUrl),
    flushIntervalMs: normalizeNumber(
      mergedConfig.flushIntervalMs,
      DEFAULT_CONFIG.flushIntervalMs,
      1000,
    ),
    maxBatchSize: normalizeNumber(
      mergedConfig.maxBatchSize,
      DEFAULT_CONFIG.maxBatchSize,
      1,
    ),
    maxQueueSize: normalizeNumber(
      mergedConfig.maxQueueSize,
      DEFAULT_CONFIG.maxQueueSize,
      10,
    ),
    maxLocalRetentionMs: normalizeNumber(
      mergedConfig.maxLocalRetentionMs,
      DEFAULT_CONFIG.maxLocalRetentionMs,
      60 * 1000,
    ),
    sampleRate: normalizeSampleRate(
      mergedConfig.sampleRate,
      DEFAULT_CONFIG.sampleRate,
    ),
    regionDetectMode: normalizeRegionDetectMode(
      mergedConfig.regionDetectMode || DEFAULT_CONFIG.regionDetectMode,
    ),
    captureRequestHeaders: normalizeBoolean(
      mergedConfig.captureRequestHeaders,
      DEFAULT_CONFIG.captureRequestHeaders,
    ),
    captureRequestData: normalizeBoolean(
      mergedConfig.captureRequestData,
      DEFAULT_CONFIG.captureRequestData,
    ),
    captureResponseHeaders: normalizeBoolean(
      mergedConfig.captureResponseHeaders,
      DEFAULT_CONFIG.captureResponseHeaders,
    ),
    captureResponseData: normalizeBoolean(
      mergedConfig.captureResponseData,
      DEFAULT_CONFIG.captureResponseData,
    ),
    rawPayloadMaxBytes: normalizeNumber(
      mergedConfig.rawPayloadMaxBytes,
      DEFAULT_CONFIG.rawPayloadMaxBytes,
      1024,
    ),
    ignoreApiPaths: normalizeIgnoreApiPaths(mergedConfig.ignoreApiPaths),
  }
}

export function isFrontendErrorLogEnabled() {
  return getFrontendErrorLogConfig().enabled
}

function isDebugEnabled() {
  return getDebugQueryValue('frontend_error_log_debug') === 'true'
}

function debugLog(...args) {
  if (isDebugEnabled())
    console.warn('[frontendErrorLog]', ...args)
}

function getUtf8ByteLength(value) {
  try {
    return new TextEncoder().encode(value).length
  }
  catch {
    try {
      return unescape(encodeURIComponent(value)).length
    }
    catch {
      return value.length
    }
  }
}

function randomId(prefix) {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID().replace(/-/g, '').slice(0, 20)}`
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${now().toString(36)}`
}

function toNullableString(value, maxLength = 255) {
  if (value === undefined || value === null || value === '')
    return null
  const text = String(value).trim()
  return text ? text.slice(0, maxLength) : null
}

function toNullableNumber(value) {
  if (value === undefined || value === null || value === '')
    return null
  const next = Number(value)
  return Number.isFinite(next) ? next : null
}

function normalizeRoutePath(value) {
  const text = String(value || '').trim()
  if (!text)
    return ''
  return text.replace(/^\/(apph5|h5)\/\1(?=\/)/i, '/$1')
}

function stripSearchFromPath(path) {
  const queryIndex = path.indexOf('?')
  const hashIndex = path.indexOf('#')
  if (queryIndex < 0)
    return path
  if (hashIndex > queryIndex) {
    return `${path.slice(0, queryIndex)}${path.slice(hashIndex)}`
  }
  return path.slice(0, queryIndex)
}

function appendRouteHistory(history, routePath, maxSize = ROUTE_STACK_MAX_SIZE) {
  const normalizedPath = normalizeRoutePath(routePath)
  const next = history.map(normalizeRoutePath).filter(Boolean)
  if (!normalizedPath || next[next.length - 1] === normalizedPath)
    return next
  next.push(normalizedPath)
  return next.slice(-Math.max(maxSize, 1))
}

function buildRouteStack(history, maxLength = ROUTE_STACK_MAX_LENGTH) {
  const normalizedHistory = history.map(normalizeRoutePath).filter(Boolean)
  if (!normalizedHistory.length)
    return undefined
  const fullText = normalizedHistory.join(' -> ')
  if (fullText.length <= maxLength)
    return fullText

  const compactHistory = normalizedHistory.map((item, index) =>
    index === normalizedHistory.length - 1 ? item : stripSearchFromPath(item),
  )
  const compactText = compactHistory.join(' -> ')
  if (compactText.length <= maxLength)
    return compactText

  let tail = compactHistory.slice()
  while (tail.length > 1) {
    tail = tail.slice(1)
    const text = ['...', ...tail].join(' -> ')
    if (text.length <= maxLength)
      return text
  }
  const currentPath = normalizedHistory[normalizedHistory.length - 1] || ''
  return currentPath.slice(0, maxLength)
}

function detectWebMobile() {
  if (typeof navigator === 'undefined')
    return false
  if (typeof navigator.userAgentData?.mobile === 'boolean') {
    return navigator.userAgentData.mobile
  }
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent || '',
  )
}

function getClientType() {
  return detectWebMobile() ? 'web' : 'pc'
}

function getPlatformType() {
  return detectWebMobile() ? 'h5' : 'pc-web'
}

function getBrowserInfo() {
  if (typeof navigator === 'undefined') {
    return { browserName: null, browserVersion: null }
  }
  const ua = navigator.userAgent || ''
  const rules = [
    [/Edg\/([\d.]+)/, 'Edge'],
    [/OPR\/([\d.]+)/, 'Opera'],
    [/Chrome\/([\d.]+)/, 'Chrome'],
    [/Firefox\/([\d.]+)/, 'Firefox'],
    [/Version\/([\d.]+)/, 'Safari'],
  ]
  for (const [pattern, name] of rules) {
    const match = ua.match(pattern)
    if (match?.[1])
      return { browserName: name, browserVersion: match[1] }
  }
  return {
    browserName: toNullableString(navigator.appName || 'Browser', 100),
    browserVersion: null,
  }
}

function getDeviceInfo() {
  if (typeof navigator === 'undefined') {
    return {
      deviceName: null,
      deviceModel: null,
      osName: null,
      osVersion: null,
      browserName: null,
      browserVersion: null,
    }
  }
  const ua = navigator.userAgent || ''
  const uaData = navigator.userAgentData
  let osName = uaData?.platform || navigator.platform || 'web'
  let deviceModel = osName
  let osVersion = null
  const android = ua.match(/Android ([\d.]+)/i)
  const androidSegment = ua.match(/Android [^)]*/i)?.[0] || ''
  const ios = ua.match(/(?:iPhone|iPad|iPod).*OS ([\d_]+)/i)
  const windows = ua.match(/Windows NT ([\d.]+)/i)
  const mac = ua.match(/Mac OS X ([\d_]+)/i)
  if (android) {
    osName = 'Android'
    osVersion = android[1]
    deviceModel = String(androidSegment.split(';')[2] || 'Android')
      .replace(/\s+Build\/.*$/i, '')
      .trim()
  }
  else if (ios) {
    osName = /iPad/i.test(ua) ? 'iPadOS' : 'iOS'
    osVersion = ios[1].replace(/_/g, '.')
    deviceModel = osName
  }
  else if (windows) {
    osName = 'Windows'
    osVersion = windows[1]
  }
  else if (mac) {
    osName = 'macOS'
    osVersion = mac[1].replace(/_/g, '.')
  }
  return {
    deviceName: toNullableString(deviceModel, 255),
    deviceModel: toNullableString(deviceModel, 255),
    osName: toNullableString(osName, 100),
    osVersion: toNullableString(osVersion, 100),
    ...getBrowserInfo(),
  }
}

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  }
  catch {
    return null
  }
}

function loadGeoipCache() {
  if (geoipCache)
    return geoipCache
  const cached = safeJsonParse(safeStorageGet(GEOIP_CACHE_KEY), null)
  if (cached?.cachedAt && now() - cached.cachedAt <= GEOIP_CACHE_TTL_MS) {
    geoipCache = cached
  }
  return geoipCache
}

function setGeoipCache(nextValue) {
  geoipCache = nextValue
  if (!nextValue) {
    safeStorageRemove(GEOIP_CACHE_KEY)
    return
  }
  safeStorageSet(GEOIP_CACHE_KEY, JSON.stringify(nextValue))
}

async function fetchGeoipInfo() {
  const mode = getFrontendErrorLogConfig().regionDetectMode
  if (mode === 'none' || mode === 'server_enrich')
    return
  try {
    const response = await fetch(GEOIP_API_URL, {
      method: 'GET',
      headers: { accept: 'application/json' },
    })
    if (!response.ok)
      throw new Error(`geoip request failed: ${response.status}`)
    const data = await response.json()
    setGeoipCache({
      ip: toNullableString(data?.ip, 100),
      regionCode: toNullableString(data?.country_code, 20),
      regionName: toNullableString(data?.country || data?.city, 100),
      timezone: toNullableString(data?.timezone, 100),
      regionSource: 'geoip_api',
      cachedAt: now(),
    })
  }
  catch (error) {
    debugLog('geoip:error', error?.message || error)
  }
}

function primeGeoipInfo() {
  const mode = getFrontendErrorLogConfig().regionDetectMode
  if (mode === 'none' || mode === 'server_enrich' || loadGeoipCache())
    return
  if (!geoipFetchPromise) {
    geoipFetchPromise = fetchGeoipInfo().finally(() => {
      geoipFetchPromise = null
    })
  }
}

function getAuthToken() {
  try {
    return getMallAppToken() || getToken() || ''
  }
  catch {
    return (
      safeStorageGet(config.mallAppTokenKey)
      || safeStorageGet(config.appTokenKey)
      || ''
    )
  }
}

function getDeviceId() {
  const cached = safeStorageGet(DEVICE_ID_KEY)
  if (cached)
    return cached
  const next = randomId('fed')
  safeStorageSet(DEVICE_ID_KEY, next)
  return next
}

function getSessionId() {
  const cached = safeJsonParse(safeStorageGet(SESSION_KEY), {})
  const expired
    = !cached.id || !cached.updatedAt || now() - cached.updatedAt > SESSION_TTL_MS
  const next = {
    id: expired ? randomId('fes') : cached.id,
    updatedAt: now(),
  }
  safeStorageSet(SESSION_KEY, JSON.stringify(next))
  return next.id
}

function maskSensitiveString(value) {
  return String(value || '')
    .replace(
      /([?&](?:token|access_token|authorization|sign|signature|password|pwd|secret|cookie)=)[^&]+/gi,
      '$1[redacted]',
    )
    .replace(/(Bearer\s+)[\w\-.~+/]+=*/gi, '$1[redacted]')
    .replace(/[\w.%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
}

function safeFrontendErrorText(value, maxLength = 300) {
  try {
    const text
      = typeof value === 'string'
        ? value
        : value?.msg || value?.message || JSON.stringify(value)
    if (!text)
      return undefined
    return maskSensitiveString(text).slice(0, maxLength)
  }
  catch {
    return undefined
  }
}

function isSensitiveKey(key) {
  return /authorization|token|cookie|password|pwd|secret|signature|sign|idcard|mobile|phone|email/i.test(
    key,
  )
}

function sanitizePayload(value, depth = 0, currentKey = '', seen = new WeakSet()) {
  if (value === undefined)
    return undefined
  if (value === null)
    return null
  if (depth >= 5)
    return '[max_depth]'
  if (isSensitiveKey(currentKey))
    return '[redacted]'
  if (typeof value === 'string')
    return maskSensitiveString(value)
  if (['number', 'boolean', 'bigint'].includes(typeof value))
    return value
  if (value instanceof Error) {
    return {
      name: value.name,
      message: maskSensitiveString(value.message || ''),
      stack: toNullableString(maskSensitiveString(value.stack || ''), 8000),
    }
  }
  if (Array.isArray(value)) {
    return value
      .slice(0, 50)
      .map(item => sanitizePayload(item, depth + 1, '', seen))
  }
  if (typeof Headers !== 'undefined' && value instanceof Headers) {
    return sanitizePayload(Object.fromEntries(value.entries()), depth, currentKey, seen)
  }
  if (typeof value === 'object') {
    if (seen.has(value))
      return '[circular]'
    seen.add(value)
    const next = {}
    Object.entries(value)
      .slice(0, 100)
      .forEach(([key, item]) => {
        next[key] = sanitizePayload(item, depth + 1, key, seen)
      })
    seen.delete(value)
    return next
  }
  return toNullableString(String(value), 4000)
}

function trimPayloadForUpload(value, maxBytes) {
  if (value === undefined || value === null)
    return null
  const sanitized = sanitizePayload(value)
  try {
    const serialized = JSON.stringify(sanitized)
    const size = getUtf8ByteLength(serialized)
    if (size <= maxBytes)
      return sanitized
    return {
      __truncated__: true,
      __bytes: size,
      preview: serialized.slice(0, Math.max(512, Math.floor(maxBytes / 2))),
    }
  }
  catch {
    return { __truncated__: true, preview: String(sanitized).slice(0, 2048) }
  }
}

function normalizeRequestUrl(url) {
  return String(url || '').trim().slice(0, 4000)
}

function getRequestPath(url) {
  const raw = normalizeRequestUrl(url)
  if (!raw)
    return null
  try {
    const parsed = new URL(raw, window.location.origin)
    return `${parsed.pathname || ''}${parsed.search || ''}`.slice(0, 500)
  }
  catch {
    return raw.slice(0, 500)
  }
}

function normalizeFetchHeaders(headers) {
  if (!headers)
    return {}
  if (typeof headers.entries === 'function')
    return Object.fromEntries(headers.entries())
  return { ...headers }
}

function appendRequestQuery(requestUrl, query) {
  if (!query || typeof query !== 'object')
    return requestUrl

  try {
    const parsedUrl = new URL(requestUrl, window.location.origin)
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null)
        return
      const values = Array.isArray(value) ? value : [value]
      values.forEach(item => parsedUrl.searchParams.append(key, String(item)))
    })
    return parsedUrl.toString()
  }
  catch {
    return requestUrl
  }
}

function buildFullFetchRequestUrl(url, options = {}) {
  const rawUrl = String(url || '')
  if (!rawUrl)
    return ''

  const resolvedUrl = /^https?:\/\//i.test(rawUrl)
    ? rawUrl
    : `${String(options.baseURL || window.location.origin).replace(/\/+$/, '')}/${rawUrl.replace(/^\/+/, '')}`

  return appendRequestQuery(resolvedUrl, options.params || options.query)
}

function getFetchBusinessFailure(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data))
    return null

  const hasSuccess = typeof data.success === 'boolean'
  const hasStatus = data.status !== undefined && data.status !== null && data.status !== ''
  const hasCode = data.code !== undefined && data.code !== null && data.code !== ''
  if (!hasSuccess && !hasStatus && !hasCode)
    return null
  if (data.success === true || data.msg === 'success')
    return null
  if ([1, '1', 200, '200'].includes(data.code))
    return null
  if (['SUCCESS', 200, '200', 300, '300'].includes(data.status))
    return null
  if (data.credentials || data.accessKeyId)
    return null

  return {
    bizCode: data.code ?? data.status,
    message: data.msg || data.message || data.mess || data,
  }
}

function reportFrontendFetchResponse(url, options, response, startedAt) {
  const failure = getFetchBusinessFailure(response?._data)
  if (!failure)
    return

  reportFrontendApiError({
    errorType: 'api_business',
    url,
    requestUrl: response?.url || buildFullFetchRequestUrl(url, options),
    method: options.method || 'GET',
    status: failure.bizCode,
    httpStatus: response?.status,
    message: failure.message,
    requestHeaders: options.headers,
    requestParams: options.params || options.query,
    requestData: options.body,
    responseHeaders: normalizeFetchHeaders(response?.headers),
    responseData: response?._data,
    bizCode: failure.bizCode,
    stage: 'business',
    rawContext: { durationMs: now() - startedAt },
  })
}

function reportFrontendFetchError(url, options, error, startedAt) {
  const response = error?.response
  const httpStatus = response?.status || error?.statusCode || error?.status
  const isTimeout = /abort|timeout/i.test(
    `${error?.name || ''} ${error?.message || ''}`,
  )
  const observedRequestUrl
    = response?.url
      || error?.request?.url
      || (typeof error?.request === 'string' ? error.request : '')
      || buildFullFetchRequestUrl(url, options)

  reportFrontendApiError({
    errorType: isTimeout ? 'api_timeout' : httpStatus ? 'api_http' : 'api_network',
    url,
    requestUrl: observedRequestUrl,
    method: options.method || 'GET',
    status: httpStatus,
    httpStatus,
    message: error?.message || response?._data || error,
    requestHeaders: options.headers,
    requestParams: options.params || options.query,
    requestData: options.body,
    responseHeaders: normalizeFetchHeaders(response?.headers),
    responseData: response?._data || error?.data,
    errorName: error?.name,
    bizCode: response?._data?.code ?? response?._data?.status,
    stage: isTimeout ? 'timeout' : httpStatus ? 'http' : 'network',
    rawContext: {
      durationMs: now() - startedAt,
      fetchErrorName: error?.name,
      fetchStatusMessage: error?.statusMessage,
    },
  })
}

export async function frontendErrorFetch(fetcher, url, options = {}) {
  if (!isClientRuntime() || !isFrontendErrorLogEnabled())
    return fetcher.raw(url, options)

  const startedAt = now()
  try {
    const response = await fetcher.raw(url, options)
    reportFrontendFetchResponse(url, options, response, startedAt)
    return response
  }
  catch (error) {
    reportFrontendFetchError(url, options, error, startedAt)
    throw error
  }
}

function normalizeMessageForFingerprint(message) {
  return String(message || '')
    .replace(/\d{2,}/g, '#')
    .replace(/[A-F0-9]{8,}/gi, '#')
    .slice(0, 300)
}

function simpleHash(value) {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index)
    hash |= 0
  }
  return Math.abs(hash).toString(36)
}

function createFingerprint(item) {
  return `fel_${simpleHash(
    [
      item.errorType,
      item.errorCode,
      item.apiPath || '',
      item.pageRoute || '',
      item.fileName || '',
      normalizeMessageForFingerprint(item.errorMessage),
    ].join('::'),
  )}`
}

function parseIsoTime(value) {
  const timestamp = Date.parse(String(value || ''))
  return Number.isFinite(timestamp) ? timestamp : null
}

function pickBoundaryIso(mode, ...values) {
  const timestamps = values.map(parseIsoTime).filter(value => value !== null)
  if (!timestamps.length)
    return null
  return new Date(
    mode === 'min' ? Math.min(...timestamps) : Math.max(...timestamps),
  ).toISOString()
}

function mergeQueueItems(existingItem, incomingItem) {
  const existingCount = Math.max(toNullableNumber(existingItem.occurrenceCount) || 1, 1)
  const incomingCount = Math.max(toNullableNumber(incomingItem.occurrenceCount) || 1, 1)
  return {
    ...existingItem,
    ...incomingItem,
    fingerprint: existingItem.fingerprint || incomingItem.fingerprint,
    occurrenceCount: existingCount + incomingCount,
    firstOccurredAt:
      pickBoundaryIso(
        'min',
        existingItem.firstOccurredAt,
        existingItem.lastOccurredAt,
        incomingItem.firstOccurredAt,
        incomingItem.lastOccurredAt,
      ) || existingItem.firstOccurredAt,
    lastOccurredAt:
      pickBoundaryIso(
        'max',
        existingItem.lastOccurredAt,
        existingItem.firstOccurredAt,
        incomingItem.lastOccurredAt,
        incomingItem.firstOccurredAt,
      ) || incomingItem.lastOccurredAt,
  }
}

function coalesceQueue(queue) {
  const result = []
  const indexByFingerprint = new Map()
  queue.forEach((entry) => {
    const fingerprint = String(entry.item?.fingerprint || '').trim()
    const existingIndex = fingerprint
      ? indexByFingerprint.get(fingerprint)
      : undefined
    if (existingIndex === undefined) {
      if (fingerprint)
        indexByFingerprint.set(fingerprint, result.length)
      result.push(entry)
      return
    }
    const existing = result[existingIndex]
    result[existingIndex] = {
      ...existing,
      createdAt: Math.min(existing.createdAt, entry.createdAt),
      retryCount: Math.max(existing.retryCount, entry.retryCount),
      nextRetryAt: Math.min(existing.nextRetryAt, entry.nextRetryAt),
      item: mergeQueueItems(existing.item, entry.item),
    }
  })
  return result
}

function pruneQueue(queue) {
  const currentConfig = getFrontendErrorLogConfig()
  const expireBefore = now() - currentConfig.maxLocalRetentionMs
  return coalesceQueue(
    (Array.isArray(queue) ? queue : []).filter(
      entry => entry?.item && Number(entry.createdAt) >= expireBefore,
    ),
  ).slice(-currentConfig.maxQueueSize)
}

function readQueue() {
  if (queueCache)
    return queueCache
  queueCache = pruneQueue(safeJsonParse(safeStorageGet(QUEUE_KEY), []))
  return queueCache
}

function writeQueue(queue) {
  queueCache = pruneQueue(queue)
  safeStorageSet(QUEUE_KEY, JSON.stringify(queueCache))
}

function getUploadUrl() {
  const customUrl = getFrontendErrorLogConfig().uploadUrl
  if (customUrl)
    return customUrl
  if (typeof window === 'undefined')
    return '/api/mall/frontserver/frontendErrorLog/batch'
  return `${window.location.origin}/api/mall/frontserver/frontendErrorLog/batch`
}

function shouldIgnoreApi(url) {
  const raw = normalizeRequestUrl(url)
  if (!raw)
    return false
  const path = getRequestPath(raw) || raw
  return getFrontendErrorLogConfig().ignoreApiPaths.some(
    pattern => raw.includes(pattern) || path.includes(pattern),
  )
}

function shouldSample() {
  const sampleRate = getFrontendErrorLogConfig().sampleRate
  if (sampleRate >= 1)
    return true
  if (sampleRate <= 0)
    return false
  return Math.random() <= sampleRate
}

function getLocale() {
  const htmlLocale
    = typeof document !== 'undefined' ? document.documentElement?.lang : ''
  if (htmlLocale)
    return htmlLocale
  const cookieLocale
    = typeof document !== 'undefined'
      ? document.cookie.match(/(?:^|;\s*)(?:site_)?i18n_current_locale=([^;]+)/)?.[1]
      : ''
  if (cookieLocale) {
    try {
      return decodeURIComponent(cookieLocale)
    }
    catch {
      return cookieLocale
    }
  }
  return typeof navigator !== 'undefined' ? navigator.language : null
}

function getAppVersion() {
  return toNullableString(getPublicRuntimeConfig().frontendErrorLogAppVersion, 50)
}

function getTopLevelContext() {
  const { store, currentServer, storeInfo } = getStoreSnapshot()
  const state = store?.$state || {}
  const siteInfo = state.siteInfo || {}
  const siteConfigData = parseObject(siteInfo.configData)
  const storePageInfo = parseObject(storeInfo.storePageInfo)
  const user = state.mallUserInfo || state.userInfo || {}
  const deviceInfo = getDeviceInfo()
  const geoInfo = loadGeoipCache() || {}
  const routePath
    = currentRouteMeta.routePath
      || (typeof window !== 'undefined'
        ? `${window.location.pathname || ''}${window.location.search || ''}`
        : null)
  const shopId
    = (typeof store?.getShopId === 'function' && store.getShopId(currentServer))
      || siteConfigData.shopId
      || siteInfo.shopId
      || storeInfo.shopId
      || storePageInfo.shopId
  const shopName
    = siteInfo.mobile_SITE_NAME
      || siteInfo.pc_SITE_NAME
      || siteInfo.title
      || storeInfo.shopName
      || storeInfo.title
      || storePageInfo.shopName
      || storePageInfo.title

  return {
    clientType: getClientType(),
    platformType: getPlatformType(),
    appVersion: getAppVersion(),
    currentServer: toNullableString(currentServer, 255),
    shopId: toNullableString(shopId, 100),
    shopName: toNullableString(shopName, 255),
    siteCode: toNullableString(
      siteInfo.siteCode || siteConfigData.siteCode || storeInfo.siteCode || storePageInfo.siteCode,
      100,
    ),
    memberCode: toNullableString(user.memberCode || user.code, 100),
    userId: toNullableString(user.id || user.userId, 100),
    sessionId: getSessionId(),
    deviceId: toNullableString(getDeviceId(), 255),
    deviceName: deviceInfo.deviceName,
    deviceModel: deviceInfo.deviceModel,
    osName: deviceInfo.osName,
    osVersion: deviceInfo.osVersion,
    browserName: deviceInfo.browserName,
    browserVersion: deviceInfo.browserVersion,
    pageRoute: toNullableString(routePath, 255),
    routeStack: toNullableString(buildRouteStack(currentRouteHistory), 1200),
    referrer:
      typeof document !== 'undefined'
        ? toNullableString(document.referrer, 255)
        : null,
    userAgent:
      typeof navigator !== 'undefined'
        ? toNullableString(navigator.userAgent, 1000)
        : null,
    networkType: toNullableString(networkType, 50),
    locale: toNullableString(getLocale(), 50),
    timezone: toNullableString(geoInfo.timezone || getTimezone(), 100),
    timezoneOffset: new Date().getTimezoneOffset(),
    clientIp: toNullableString(geoInfo.ip, 100),
    regionCode: toNullableString(geoInfo.regionCode, 50),
    regionName: toNullableString(geoInfo.regionName, 100),
    regionSource: toNullableString(geoInfo.regionSource, 50),
  }
}

function inferApiErrorType(input) {
  if (input.errorType)
    return input.errorType
  const stage = String(input.stage || '').toLowerCase()
  if (stage === 'business')
    return 'api_business'
  if (stage === 'http')
    return 'api_http'
  if (stage === 'timeout')
    return 'api_timeout'
  return 'api_network'
}

function inferApiErrorCode(input, errorType) {
  if (input.errorCode)
    return String(input.errorCode)
  if (input.bizCode !== undefined && input.bizCode !== null && input.bizCode !== '') {
    return `BIZ_${input.bizCode}`
  }
  const status = toNullableNumber(input.httpStatus ?? input.status)
  if (status)
    return `HTTP_${status}`
  if (
    errorType === 'api_timeout'
    || /timeout|abort/i.test(`${input.errorName || ''} ${input.message || ''}`)
  ) {
    return 'REQUEST_TIMEOUT'
  }
  return 'NETWORK_ERROR'
}

function extractHeaderValue(headers, names) {
  if (!headers)
    return null
  for (const name of names) {
    if (typeof headers.get === 'function') {
      const value = headers.get(name)
      if (value)
        return value
    }
    const directValue = headers[name] || headers[name.toLowerCase()]
    if (directValue)
      return directValue
  }
  return null
}

function extractTraceId(input) {
  return toNullableString(
    input.traceId
    || extractHeaderValue(input.responseHeaders, ['x-trace-id', 'traceid'])
    || get(input.responseData, 'traceId')
    || get(input.responseData, 'trace_id'),
    100,
  )
}

function extractRequestId(input) {
  return toNullableString(
    input.requestId
    || extractHeaderValue(input.responseHeaders, ['x-request-id', 'requestid'])
    || get(input.responseData, 'requestId')
    || get(input.responseData, 'request_id'),
    100,
  )
}

function enqueueItem(item) {
  const queue = readQueue()
  const existingIndex = queue.findIndex(
    entry => entry.item.fingerprint === item.fingerprint,
  )
  if (existingIndex >= 0) {
    const existing = queue[existingIndex]
    queue[existingIndex] = {
      ...existing,
      item: mergeQueueItems(existing.item, item),
    }
  }
  else {
    queue.push({
      id: randomId('feq'),
      createdAt: now(),
      retryCount: 0,
      nextRetryAt: now(),
      item,
    })
  }
  writeQueue(queue)
  debugLog('enqueue', item.errorType, item.errorCode, item.errorMessage)
  scheduleFlush(800)
}

function scheduleFlush(delay) {
  if (flushTimer)
    clearTimeout(flushTimer)
  flushTimer = setTimeout(() => {
    flushTimer = null
    flushFrontendErrorLogQueue().catch(() => undefined)
  }, delay ?? getFrontendErrorLogConfig().flushIntervalMs)
}

function bumpRetry(ids) {
  const nextQueue = readQueue().map((entry) => {
    if (!ids.includes(entry.id))
      return entry
    const retryCount = entry.retryCount + 1
    return {
      ...entry,
      retryCount,
      nextRetryAt:
        now()
        + Math.min(BASE_RETRY_MS * 2 ** (retryCount - 1), MAX_RETRY_MS),
    }
  })
  writeQueue(nextQueue)
  const nextRetryAt = Math.min(...nextQueue.map(entry => entry.nextRetryAt))
  if (Number.isFinite(nextRetryAt)) {
    scheduleFlush(Math.max(nextRetryAt - now(), BASE_RETRY_MS))
  }
}

async function sendBatch(entries) {
  const headers = { 'Content-Type': 'application/json' }
  const token = getAuthToken()
  if (token)
    headers.authorization = token
  const controller
    = typeof AbortController !== 'undefined' ? new AbortController() : undefined
  const timer = controller
    ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null
  const requestBody = JSON.stringify({
    batchId: randomId('feb'),
    clientType: getClientType(),
    platformType: getPlatformType(),
    appVersion: getAppVersion(),
    items: entries.map(entry => ({ ...entry.item, recordKey: entry.id })),
  })

  try {
    const response = await fetch(getUploadUrl(), {
      method: 'POST',
      headers,
      body: requestBody,
      credentials: 'same-origin',
      keepalive: getUtf8ByteLength(requestBody) <= WEB_KEEPALIVE_MAX_BYTES,
      signal: controller?.signal,
    })
    if (!response.ok) {
      throw new Error(`frontend error upload failed: ${response.status}`)
    }
    const data = await response.json().catch(() => ({ success: true }))
    if (data?.success === false) {
      throw new Error(data.message || 'frontend error upload failed')
    }
    debugLog('sendBatch:success', data)
    return data
  }
  finally {
    if (timer)
      clearTimeout(timer)
  }
}

export async function flushFrontendErrorLogQueue() {
  if (!isClientRuntime() || !isFrontendErrorLogEnabled() || flushing || !online)
    return
  flushing = true
  let attemptedIds = []
  try {
    const currentConfig = getFrontendErrorLogConfig()
    const queue = readQueue()
    const readyEntries = queue
      .filter(entry => entry.nextRetryAt <= now())
      .slice(0, currentConfig.maxBatchSize)
    if (!readyEntries.length) {
      const nextRetryAt = Math.min(...queue.map(entry => entry.nextRetryAt))
      if (Number.isFinite(nextRetryAt)) {
        scheduleFlush(Math.max(nextRetryAt - now(), 250))
      }
      return
    }
    attemptedIds = readyEntries.map(entry => entry.id)
    await sendBatch(readyEntries)
    const sentIds = new Set(attemptedIds)
    writeQueue(queue.filter(entry => !sentIds.has(entry.id)))
    if (readQueue().some(entry => entry.nextRetryAt <= now()))
      scheduleFlush(500)
  }
  catch (error) {
    debugLog('sendBatch:error', error?.message || error)
    if (attemptedIds.length)
      bumpRetry(attemptedIds)
    throw error
  }
  finally {
    flushing = false
  }
}

export function syncFrontendErrorRoute(meta = {}) {
  const routePath = toNullableString(meta.routePath, 255)
  currentRouteMeta = {
    routeName: toNullableString(meta.routeName, 100),
    routePath,
  }
  currentRouteHistory = appendRouteHistory(currentRouteHistory, routePath)
}

function buildApiErrorItem(input) {
  if (!shouldSample())
    return null
  const errorType = inferApiErrorType(input)
  const errorCode = inferApiErrorCode(input, errorType)
  const context = getTopLevelContext()
  const currentConfig = getFrontendErrorLogConfig()
  const requestUrl = normalizeRequestUrl(input.requestUrl || input.url || '')
  if (shouldIgnoreApi(requestUrl))
    return null
  const apiPath = getRequestPath(requestUrl)
  const message
    = safeFrontendErrorText(input.message, 1000)
      || safeFrontendErrorText(input.responseData, 1000)
      || `${errorType}:${errorCode}`
  const occurredAt = new Date().toISOString()
  const httpStatus = toNullableNumber(input.httpStatus ?? input.status)
  const item = {
    ...context,
    pageRoute: toNullableString(
      input.pageRoute || currentRouteMeta.routePath || context.pageRoute,
      255,
    ),
    errorType,
    errorCode,
    errorName: toNullableString(input.errorName, 100),
    errorMessage: message,
    stack: toNullableString(
      safeFrontendErrorText(input.stack || input.message, 8000),
      8000,
    ),
    apiPath: toNullableString(apiPath, 255),
    apiMethod: toNullableString(String(input.method || 'GET').toUpperCase(), 20),
    requestUrl: toNullableString(requestUrl, 2000),
    requestHeaders: currentConfig.captureRequestHeaders
      ? trimPayloadForUpload(input.requestHeaders, currentConfig.rawPayloadMaxBytes)
      : null,
    requestParams: currentConfig.captureRequestData
      ? trimPayloadForUpload(input.requestParams, currentConfig.rawPayloadMaxBytes)
      : null,
    requestData: currentConfig.captureRequestData
      ? trimPayloadForUpload(input.requestData, currentConfig.rawPayloadMaxBytes)
      : null,
    httpStatus,
    responseHeaders: currentConfig.captureResponseHeaders
      ? trimPayloadForUpload(input.responseHeaders, currentConfig.rawPayloadMaxBytes)
      : null,
    responseData: currentConfig.captureResponseData
      ? trimPayloadForUpload(input.responseData, currentConfig.rawPayloadMaxBytes)
      : null,
    traceId: extractTraceId(input),
    requestId: extractRequestId(input),
    bizCode:
      input.bizCode !== undefined && input.bizCode !== null && input.bizCode !== ''
        ? input.bizCode
        : httpStatus,
    occurrenceCount: 1,
    firstOccurredAt: occurredAt,
    lastOccurredAt: occurredAt,
    rawContext: trimPayloadForUpload(
      { stage: input.stage, ...(input.rawContext || {}) },
      currentConfig.rawPayloadMaxBytes,
    ),
  }
  item.fingerprint = createFingerprint(item)
  return item
}

function buildHandledErrorItem(input) {
  if (!shouldSample())
    return null
  const context = getTopLevelContext()
  const errorType = input.errorType || 'handled'
  const occurredAt = new Date().toISOString()
  const item = {
    ...context,
    pageRoute: toNullableString(
      input.pageRoute || currentRouteMeta.routePath || context.pageRoute,
      255,
    ),
    errorType,
    errorCode: toNullableString(input.errorCode, 100) || 'HANDLED_ERROR',
    errorName: toNullableString(input.errorName, 100),
    errorMessage:
      safeFrontendErrorText(input.message, 1000) || `${errorType}:unknown`,
    stack: toNullableString(
      safeFrontendErrorText(input.stack || input.message, 8000),
      8000,
    ),
    fileName: toNullableString(input.fileName, 255),
    lineNo: toNullableNumber(input.lineNo),
    colNo: toNullableNumber(input.colNo),
    occurrenceCount: 1,
    firstOccurredAt: occurredAt,
    lastOccurredAt: occurredAt,
    rawContext: trimPayloadForUpload(
      input.rawContext,
      getFrontendErrorLogConfig().rawPayloadMaxBytes,
    ),
  }
  item.fingerprint = createFingerprint(item)
  return item
}

export function reportFrontendApiError(input = {}) {
  if (!isClientRuntime() || !isFrontendErrorLogEnabled())
    return
  initializeFrontendErrorLog()
  const item = buildApiErrorItem(input)
  if (item)
    enqueueItem(item)
}

export function reportFrontendHandledError(input = {}) {
  if (!isClientRuntime() || !isFrontendErrorLogEnabled())
    return
  initializeFrontendErrorLog()
  const item = buildHandledErrorItem(input)
  if (item)
    enqueueItem(item)
}

function setupWebRuntimeListeners() {
  if (typeof window === 'undefined')
    return
  window.addEventListener(
    'error',
    (event) => {
      const target = event.target
      if (target && target !== window && typeof target.tagName === 'string') {
        const tagName = target.tagName.toLowerCase()
        reportFrontendHandledError({
          errorType: 'resource_load',
          errorCode: 'RESOURCE_LOAD',
          errorName: `${tagName}_load_error`,
          message: `${tagName} resource load failed`,
          rawContext: { tagName, resourceUrl: target.src || target.href || '' },
        })
        return
      }
      reportFrontendHandledError({
        errorType: 'js_runtime',
        errorCode: 'JS_RUNTIME',
        errorName: toNullableString(event.error?.name, 100),
        message: event.message || event.error,
        stack: toNullableString(event.error?.stack, 8000),
        fileName: toNullableString(event.filename, 255),
        lineNo: toNullableNumber(event.lineno),
        colNo: toNullableNumber(event.colno),
      })
    },
    true,
  )
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    reportFrontendHandledError({
      errorType: 'promise_rejection',
      errorCode: 'UNHANDLED_REJECTION',
      errorName: toNullableString(reason?.name, 100),
      message: reason,
      stack: toNullableString(reason?.stack, 8000),
      rawContext: reason,
    })
  })
  window.addEventListener('online', () => {
    online = true
    updateNetworkType()
    scheduleFlush(200)
  })
  window.addEventListener('offline', () => {
    online = false
    networkType = 'offline'
  })
  window.addEventListener('pagehide', () => {
    flushFrontendErrorLogQueue().catch(() => undefined)
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushFrontendErrorLogQueue().catch(() => undefined)
    }
  })
}

function updateNetworkType() {
  const connection
    = typeof navigator !== 'undefined'
      ? navigator.connection || navigator.mozConnection || navigator.webkitConnection
      : null
  networkType
    = connection?.effectiveType
      || connection?.type
      || (typeof navigator !== 'undefined' && navigator.onLine === false
        ? 'offline'
        : 'online')
}

export function initializeFrontendErrorLog() {
  if (initialized || typeof window === 'undefined')
    return
  initialized = true
  getDeviceId()
  getSessionId()
  loadGeoipCache()
  online = navigator.onLine !== false
  updateNetworkType()
  setupWebRuntimeListeners()
  if (isFrontendErrorLogEnabled())
    primeGeoipInfo()
  if (readQueue().length > 0)
    scheduleFlush(1200)
}
